import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * DebugLoggerService
 *
 * Centralized logging service that respects environment debug mode.
 * - In development: debugMode = true → All logs are shown
 * - In staging/production: debugMode = false → Logs are suppressed
 *
 * Usage:
 * ```typescript
 * constructor(private debugLogger: DebugLoggerService) {}
 *
 * this.debugLogger.log('🔄 Data loaded', data);
 * this.debugLogger.warn('⚠️ Warning message', details);
 * this.debugLogger.error('❌ Error occurred', error);
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class DebugLoggerService {
  private readonly debugMode: boolean;

  constructor() {
    this.debugMode = environment.debugMode ?? !environment.production;
  }

  /**
   * Log a message to console (only in debug mode)
   * @param message - The message to log
   * @param optionalParams - Additional parameters to log
   */
  log(message?: any, ...optionalParams: any[]): void {
    if (this.debugMode) {
    }
  }

  /**
   * Log a warning to console (only in debug mode)
   * @param message - The warning message
   * @param optionalParams - Additional parameters to log
   */
  warn(message?: any, ...optionalParams: any[]): void {
    if (this.debugMode) {
      console.warn(message, ...optionalParams);
    }
  }

  /**
   * Log an error to console (always shown, even in production)
   * Errors are critical and should always be logged
   * @param message - The error message
   * @param optionalParams - Additional parameters to log
   */
  error(message?: any, ...optionalParams: any[]): void {
    // ✅ Always log errors, even in production
    console.error(message, ...optionalParams);
  }

  /**
   * Log an info message to console (only in debug mode)
   * @param message - The info message
   * @param optionalParams - Additional parameters to log
   */
  info(message?: any, ...optionalParams: any[]): void {
    if (this.debugMode) {
      console.info(message, ...optionalParams);
    }
  }

  /**
   * Log a debug message to console (only in debug mode)
   * @param message - The debug message
   * @param optionalParams - Additional parameters to log
   */
  debug(message?: any, ...optionalParams: any[]): void {
    if (this.debugMode) {
      console.debug(message, ...optionalParams);
    }
  }

  /**
   * Log a table to console (only in debug mode)
   * Useful for displaying arrays or objects in a table format
   * @param tabularData - The data to display in table format
   * @param properties - Optional array of properties to display
   */
  table(tabularData?: any, properties?: string[]): void {
    if (this.debugMode) {
      console.table(tabularData, properties);
    }
  }

  /**
   * Start a timer (only in debug mode)
   * @param label - The label for the timer
   */
  time(label?: string): void {
    if (this.debugMode) {
      console.time(label);
    }
  }

  /**
   * End a timer and log the elapsed time (only in debug mode)
   * @param label - The label for the timer
   */
  timeEnd(label?: string): void {
    if (this.debugMode) {
      console.timeEnd(label);
    }
  }

  /**
   * Create a group in the console (only in debug mode)
   * @param groupTitle - The title of the group
   */
  group(groupTitle?: string): void {
    if (this.debugMode) {
      console.group(groupTitle);
    }
  }

  /**
   * Create a collapsed group in the console (only in debug mode)
   * @param groupTitle - The title of the group
   */
  groupCollapsed(groupTitle?: string): void {
    if (this.debugMode) {
      console.groupCollapsed(groupTitle);
    }
  }

  /**
   * End a console group (only in debug mode)
   */
  groupEnd(): void {
    if (this.debugMode) {
      console.groupEnd();
    }
  }

  /**
   * Check if debug mode is enabled
   * @returns true if debug mode is enabled
   */
  isDebugMode(): boolean {
    return this.debugMode;
  }

  /**
   * Log environment information (only in debug mode)
   */
  logEnvironmentInfo(): void {
    if (this.debugMode) {
      console.group('🌍 Environment Information');
      console.groupEnd();
    }
  }
}

